/**
 * VisitDetailsModal Component
 *
 * Displays detailed information about a visit in a modal dialog.
 * Used primarily for map-based visit viewing.
 *
 * Follows Clean Architecture principles:
 * - Uses reusable components (DetailsModal, ImageViewer)
 * - Uses Typography components for consistent styling
 * - Separates concerns (UI logic vs business logic)
 * - Follows project patterns and conventions
 */

import React, {useState, useEffect} from 'react';
import {createPortal} from 'react-dom';
import {Visit} from '../../../features/visits/models/visit.model';
import {
  Calendar,
  User,
  Clock,
  Package,
  Camera,
  MessageSquare,
  MapPin,
  Phone,
  Mail,
  Eye,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import {formatGregorianDateTime} from '../../utils/dateUtils';
import {useVisitProductDetails} from '../../../features/visits/hooks/useVisitProductDetails';
import DetailsModal from './DetailsModal';
import {
  AppTitle,
  AppCardTitle,
  AppSectionTitle,
  AppText,
  AppBodyText,
  AppSmallText,
  AppCaption
} from '../ui/Typography';

interface VisitDetailsModalProps {
  visit: Visit | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Custom hook for managing image lightbox state and keyboard navigation
 */
const useImageLightbox = (images: Visit['visitImages']) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImageIndex(null);
  };

  const goToPrevious = () => {
    if (selectedImageIndex !== null && images) {
      setSelectedImageIndex(selectedImageIndex > 0 ? selectedImageIndex - 1 : images.length - 1);
    }
  };

  const goToNext = () => {
    if (selectedImageIndex !== null && images) {
      setSelectedImageIndex(selectedImageIndex < images.length - 1 ? selectedImageIndex + 1 : 0);
    }
  };

  // Global keyboard event listener for lightbox navigation
  useEffect(() => {
    if (selectedImageIndex !== null && images) {
      const handleGlobalKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          closeLightbox();
        } else if (e.key === 'ArrowLeft') {
          goToPrevious();
        } else if (e.key === 'ArrowRight') {
          goToNext();
        }
      };

      document.addEventListener('keydown', handleGlobalKeyDown);
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleGlobalKeyDown);
        document.body.style.overflow = '';
      };
    }
  }, [selectedImageIndex, images]);

  return {
    selectedImageIndex,
    openLightbox,
    closeLightbox,
    goToPrevious,
    goToNext
  };
};

const VisitDetailsModal: React.FC<VisitDetailsModalProps> = ({visit, isOpen, onClose}) => {
  const {productDetails, loading: productsLoading, error: productsError, fetchProductDetails} = useVisitProductDetails();

  // Fetch product details when visit changes
  useEffect(() => {
    if (isOpen && visit?.visitItems && visit.visitItems.length > 0) {
      const productIds = visit.visitItems.map(item => item.productId);
      const missingProductIds = productIds.filter(id => !productDetails[id]);
      if (missingProductIds.length > 0) {
        fetchProductDetails(missingProductIds);
      }
    }
  }, [isOpen, visit?.visitItems, fetchProductDetails, productDetails]);

  // Image lightbox management
  const {selectedImageIndex, openLightbox, closeLightbox, goToPrevious, goToNext} = useImageLightbox(visit?.visitImages);

  if (!isOpen || !visit) {return null;}

  // Prepare images for ImageViewer component
  const visitImages = visit.visitImages?.map(img => ({
    id: img.id,
    imageUrl: img.imageUrl,
    description: img.description,
    imageType: img.imageType
  })) || [];

  const formatDate = (dateString: string) => formatGregorianDateTime(dateString);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeLightbox();
    } else if (e.key === 'ArrowLeft') {
      goToPrevious();
    } else if (e.key === 'ArrowRight') {
      goToNext();
    }
  };

  return (
    <>
      <DetailsModal
        isOpen={isOpen}
        onClose={onClose}
        title={`تفاصيل الزيارة #${visit.id}`}
        width="max-w-4xl"
      >
        <div className="space-y-6">
          {/* Header Section with Status */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-brand-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-brand-100 rounded-full">
                  <Calendar className="h-6 w-6 text-brand-600" />
                </div>
                <div>
                  <AppTitle className="text-gray-900">تفاصيل الزيارة</AppTitle>
                  <AppBodyText className="text-gray-600">تم إنشاؤها في {formatDate(visit.createdAt)}</AppBodyText>
                </div>
              </div>
              <div className="text-right">
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${visit.statusColor}`}>
                  {visit.statusText}
                </span>
              </div>
            </div>
          </div>

          {/* Basic Information Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Store Information */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-brand-100 rounded-lg">
                  <MapPin className="h-5 w-5 text-brand-600" />
                </div>
                <AppCardTitle>معلومات المتجر</AppCardTitle>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-brand-500 rounded-full"></div>
                  <div>
                    <AppSmallText className="text-gray-600">اسم المتجر</AppSmallText>
                    <AppText className="font-semibold text-gray-900">{visit.store.storeName}</AppText>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <AppCaption className="text-gray-500 mb-1">المدينة</AppCaption>
                    <AppText className="font-medium">{visit.store.city}</AppText>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <AppCaption className="text-gray-500 mb-1">المنطقة</AppCaption>
                    <AppText className="font-medium">{visit.store.region}</AppText>
                  </div>
                </div>
                {visit.store.managerName && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <AppText className="text-xs text-green-600 mb-1">مدير المتجر</AppText>
                    <AppText className="font-medium text-green-900">{visit.store.managerName}</AppText>
                  </div>
                )}
              </div>
            </div>

            {/* Visitor Information */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-green-100 rounded-lg">
                  <User className="h-5 w-5 text-green-600" />
                </div>
                <AppCardTitle>معلومات المسوق</AppCardTitle>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div>
                    <AppText className="text-sm text-gray-600">الاسم الكامل</AppText>
                    <AppText className="font-semibold text-gray-900">{visit.visitor.fullName}</AppText>
                  </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <AppText className="text-xs text-gray-500 mb-1">اسم المستخدم</AppText>
                  <AppText className="font-medium">@{visit.visitor.username}</AppText>
                </div>
                {visit.visitor.email && (
                  <div className="p-3 bg-brand-50 rounded-lg border border-brand-200">
                    <AppText className="text-xs text-brand-600 mb-1">البريد الإلكتروني</AppText>
                    <div className="flex items-center gap-2">
                      <Mail className="w-3 h-3 text-brand-600" />
                      <AppText className="font-medium text-brand-900">{visit.visitor.email}</AppText>
                    </div>
                  </div>
                )}
                {visit.visitor.phoneNumber && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <AppText className="text-xs text-green-600 mb-1">رقم الهاتف</AppText>
                    <div className="flex items-center gap-2">
                      <Phone className="w-3 h-3 text-green-600" />
                      <AppText className="font-medium text-green-900">{visit.visitor.phoneNumber}</AppText>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Visit Details Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white border border-brand-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-brand-100 rounded-lg">
                  <Calendar className="h-5 w-5 text-brand-600" />
                </div>
                <AppCardTitle>تاريخ الزيارة</AppCardTitle>
              </div>
              <div className="space-y-3">
                <div className="p-4 bg-brand-50 rounded-lg">
                  <AppText className="text-sm text-brand-600 mb-1">تاريخ الزيارة</AppText>
                  <AppText className="font-semibold text-lg text-brand-900">{formatDate(visit.visitDate)}</AppText>
                </div>
                {visit.updatedAt !== visit.createdAt && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <AppText className="text-xs text-gray-500 mb-1">آخر تحديث</AppText>
                    <AppText className="font-medium text-gray-700">{formatDate(visit.updatedAt)}</AppText>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white border border-purple-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Package className="h-5 w-5 text-purple-600" />
                </div>
                <AppCardTitle>المنتجات</AppCardTitle>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">{visit.visitItems?.length || 0}</div>
                <AppText className="text-sm text-gray-600">منتج محدث</AppText>
              </div>
            </div>

            <div className="bg-white border border-orange-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Camera className="h-5 w-5 text-orange-600" />
                </div>
                <AppCardTitle>الصور</AppCardTitle>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600 mb-2">{visit.visitImages?.length || 0}</div>
                <AppText className="text-sm text-gray-600">صورة مرفقة</AppText>
              </div>
            </div>
          </div>

          {/* Visit Items Details */}
          {visit.visitItems && visit.visitItems.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Package className="h-5 w-5 text-purple-600" />
                </div>
                <AppSectionTitle>تفاصيل المنتجات المحدثة</AppSectionTitle>
                <div className="ml-auto">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                    {visit.visitItems.length} منتج
                  </span>
                </div>
              </div>

              {productsError && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AppText className="text-sm text-yellow-800">
                    تحذير: لا يمكن تحميل تفاصيل بعض المنتجات. سيتم عرض معرفات المنتجات بدلاً من الأسماء.
                  </AppText>
                </div>
              )}

              <div className="grid gap-4">
                {visit.visitItems.map((item, index) => {
                  const product = productDetails[item.productId];
                  const productName = product?.name || item.productName || `منتج #${item.productId}`;
                  const brandName = visit.brand?.name || `العلامة التجارية #${item.brandId}`;

                  return (
                    <div
                      key={item.id}
                      className="p-4 rounded-lg border-2 bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            {productsLoading ? (
                              <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <span className="text-sm font-bold text-purple-600">{index + 1}</span>
                            )}
                          </div>
                          <div>
                            <AppText className="font-semibold text-gray-900">{productName}</AppText>
                            <AppText className="text-sm text-gray-600">العلامة التجارية: {brandName}</AppText>
                          </div>
                        </div>
                        <div className="text-right">
                          <AppText className="text-xs text-gray-500 mb-1">التغيير</AppText>
                          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                            item.quantityChange > 0 ? 'bg-green-100 text-green-800' :
                            item.quantityChange < 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {item.quantityChange > 0 ? '+' : ''}{item.quantityChange}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white p-3 rounded-lg border border-gray-200">
                          <AppText className="text-xs text-gray-500 mb-1">الكمية السابقة</AppText>
                          <AppText className="font-semibold text-lg text-gray-700">{item.previousQuantity}</AppText>
                        </div>
                        <div className="bg-white p-3 rounded-lg border border-gray-200">
                          <AppText className="text-xs text-gray-500 mb-1">الكمية الجديدة</AppText>
                          <AppText className="font-semibold text-lg text-gray-700">{item.newQuantity}</AppText>
                        </div>
                      </div>

                      {item.notes && (
                        <div className="mt-3 p-3 bg-brand-50 rounded-lg border border-brand-200">
                          <AppText className="text-xs text-brand-600 font-medium mb-1">ملاحظات</AppText>
                          <AppText className="text-sm text-brand-800">{item.notes}</AppText>
                        </div>
                      )}
                      {/* NOTE: visits do not carry per-item serial numbers. The
                          backend visit item exposes only a `has_serial` boolean
                          (product is serial-tracked), never the serials, and
                          neither VisitItem nor its DTO has a serials field. The
                          previous `item.serialNumbers` block was dead code (always
                          undefined) and never rendered. Showing serials per visit
                          would require a backend change (a new feature). */}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Visit Notes */}
          {visit.notes && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-brand-100 rounded-lg">
                  <MessageSquare className="h-5 w-5 text-brand-600" />
                </div>
                <AppSectionTitle>الملاحظات</AppSectionTitle>
              </div>
              <div className="p-4 bg-brand-50 rounded-lg border border-brand-200">
                <AppText className="text-gray-800 leading-relaxed">{visit.notes}</AppText>
              </div>
            </div>
          )}

          {/* Images */}
          {visitImages.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Camera className="h-5 w-5 text-orange-600" />
                </div>
                <AppSectionTitle>صور الزيارة</AppSectionTitle>
                <div className="ml-auto">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                    {visitImages.length} صورة
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {visitImages.map((image, index) => (
                  <div key={image.id} className="relative group">
                    <div
                      className="relative cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        openLightbox(index);
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          openLightbox(index);
                        }
                      }}
                    >
                      <img
                        src={image.imageUrl}
                        alt={image.description || `صورة ${image.imageType}`}
                        className="w-full h-32 object-cover rounded-lg border border-gray-200 hover:shadow-lg transition-shadow pointer-events-none"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center pointer-events-none">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <Eye className="w-6 h-6 text-white" />
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <AppSmallText className="text-gray-600 font-medium">{image.imageType}</AppSmallText>
                      {image.description && (
                        <AppSmallText className="text-gray-500 truncate">{image.description}</AppSmallText>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DetailsModal>

      {/* Image Lightbox Modal - Rendered via Portal to ensure it appears above map */}
      {selectedImageIndex !== null && visit.visitImages && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
          style={{
            position: 'fixed',
            zIndex: 9999,
            pointerEvents: 'auto',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
          }}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            aria-label="إغلاق"
          >
            <X className="w-8 h-8" />
          </button>

          {visit.visitImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10"
                aria-label="الصورة السابقة"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10"
                aria-label="الصورة التالية"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}

          <div className="max-w-4xl max-h-[80vh] mx-4">
            <img
              src={visit.visitImages[selectedImageIndex].imageUrl}
              alt={visit.visitImages[selectedImageIndex].description || `صورة ${selectedImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
              }}
            />
          </div>

          {visit.visitImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
              {selectedImageIndex + 1} / {visit.visitImages.length}
            </div>
          )}

          {visit.visitImages[selectedImageIndex].description && (
            <div className="absolute bottom-4 left-4 text-white text-sm max-w-md">
              <div className="bg-black bg-opacity-50 rounded-lg p-3">
                <div className="text-xs text-gray-300 mb-1">{visit.visitImages[selectedImageIndex].imageType}</div>
                <div>{visit.visitImages[selectedImageIndex].description}</div>
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default VisitDetailsModal;
